/**
 * Shared chip-picker primitive for the Domain tags / Your tags filters.
 *
 * Behavior (top → bottom inside the FilterSection body):
 *
 *   [🔍 Filter tags…                                          ]   ← only when >6 options
 *   ┌─ max-h-48 overflow-y-auto ─────────────────────────────┐
 *   │ [● selected_a ✕] [● selected_b ✕]   ← pinned, in selection order
 *   │ [● top_by_count]  [● next]  …       ← unselected, count desc, payload order for ties
 *   └─────────────────────────────────────────────────────────┘
 *
 * Sort order: pinned selected first, then unselected by count desc, ties left
 * in payload order (see the `rest.sort` note). This surfaces high-leverage tags without
 * a "show more" disclosure, and guarantees the user always sees what's
 * currently filtered even when the scroll cap would otherwise hide it.
 *
 * Dim-when-zero: a chip recedes when it's unselected, not present in the
 * current filtered graph view, AND the user hasn't selected anything yet.
 * The condition mirrors what was already in each filter - keeps "tags that
 * exist but won't help right now" visible without removing them.
 *
 * The recession is applied to the chip's CHROME (a faded border and a muted
 * fill), never to its text. It used to be a flat `opacity-40` on the whole
 * button, which blended the label down to 2.68:1 and the count to 1.61:1
 * against the panel - both well under WCAG AA's 4.5:1 for text this size, and
 * caught by the axe gate on `Components/Mindspace/Filters/TagChipPicker`.
 * Dimming a control's text to signal "less relevant" is the thing AA exists to
 * stop: the hint is advisory, the chip stays clickable, and a user who cannot
 * read it has lost the option rather than been advised about it.
 *
 * For the same reason the concept count is coloured `text-muted-foreground` at
 * full opacity rather than inheriting the label colour at `opacity-50` (3.5:1
 * unselected, 2.31:1 selected). Subordination is carried by hue and weight,
 * which cost no contrast.
 */

import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import type { TagOption } from '@/lib/api-client';
import { useFilterLayout } from '@/components/features/mindspace/filters/filterLayout';
import {
  chipCount,
  filterChip,
} from '@/components/features/mindspace/filters/filterStyles';

const TYPEAHEAD_THRESHOLD = 6;

/** Tags the phone sheet shows before "Show all N" - selected ones always. */
const SHEET_TAGS = 12;

/** The chip list's scroll cap per size. None in the phone sheet: a scroller
 *  inside the sheet's own scroll is two scrollers under one thumb. */
const MAX_H = {
  sm: 'max-h-48 overflow-y-auto',
  md: 'max-h-60 overflow-y-auto',
  lg: 'max-h-72 overflow-y-auto',
  // 8px between wrapped rows: a 36px chip's 44px tap area reaches 4px past
  // each edge, and at 6px the next row's took the overlap.
  sheet: 'gap-y-2',
} as const;

interface TagChipPickerProps {
  options: TagOption[];
  selected: string[];
  onToggle: (tag: string) => void;
  /** Tags present in the current filtered graph - for the dim-when-zero hint. */
  availableInView: Set<string>;
  /** Optional leading color dot. User tags omit this; domain tags pass the palette swatch. */
  colorFor?: (tag: string) => string;
  isLoading: boolean;
  /** Shown when `options.length === 0` (nothing has been tagged yet at all). */
  emptyLabel: string;
  searchPlaceholder?: string;
  /** Opt out of the "dim when not in current view" hint. The hint
   *  reads as feedback ("this tag won't filter anything") in the
   *  Knowledge Archive sidebar where chip selection drives the graph;
   *  in surfaces like the Custom-session modal there's no graph state
   *  to compare against, so every unselected chip recedes and the
   *  picker reads as "faded out." Default: dim (legacy behaviour). */
  dimUnavailable?: boolean;
  /** Max-height tier for the scrollable chip container. The default
   *  (`sm`) is tuned for the narrow Knowledge Archive sidebar; modals
   *  get more room. */
  size?: 'sm' | 'md' | 'lg';
}

export function TagChipPicker({
  options,
  selected,
  onToggle,
  availableInView,
  colorFor,
  isLoading,
  emptyLabel,
  searchPlaceholder = 'Filter tags…',
  dimUnavailable = true,
  size = 'sm',
}: TagChipPickerProps) {
  const [search, setSearch] = useState('');
  const sheet = useFilterLayout() === 'sheet';
  const [expanded, setExpanded] = useState(false);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Split + sort. Selected chips render first in user-selection order;
  // unselected chips fall through to count-desc with alpha tiebreak.
  // The split happens BEFORE typeahead filtering so a selected chip
  // pinned at top can still get filtered out if the user types a name
  // it doesn't match - predictable, matches "filter shows what matches".
  const sortedOptions = useMemo(() => {
    const sel: TagOption[] = [];
    const rest: TagOption[] = [];
    for (const opt of options) {
      (selectedSet.has(opt.tag) ? sel : rest).push(opt);
    }
    // Selected chips: pin in count-desc order so the most-prominent
    // picks lead the row (previously preserved click order, but the
    // user's pick order is rarely the order they want to scan back in).
    sel.sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
    // Unselected chips: count-desc primary. Stable-sort ties to the
    // backend's order rather than alpha so a fresh mindspace (where
    // most tags share count=1) doesn't visually fake-alphabetize.
    rest.sort((a, b) => b.count - a.count);
    return [...sel, ...rest];
  }, [options, selected, selectedSet]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return sortedOptions;
    const needle = search.trim().toLowerCase();
    return sortedOptions.filter(o => o.tag.toLowerCase().includes(needle));
  }, [sortedOptions, search]);

  if (isLoading) {
    return <p className='py-1 text-xs text-muted-foreground'>Loading…</p>;
  }

  if (options.length === 0) {
    return <p className='py-1 text-xs text-muted-foreground'>{emptyLabel}</p>;
  }

  const isLarge = size !== 'sm';
  // In the modal contexts the search bar is always useful; in the narrow
  // sidebar (sm) we keep the original threshold so it doesn't push chips
  // below the fold for short lists.
  const showTypeahead = isLarge || options.length > TYPEAHEAD_THRESHOLD;
  const maxHCls = MAX_H[sheet ? 'sheet' : size];

  // Split out selected vs unselected for the modal layout. The sidebar
  // keeps the single-list treatment (selected sort to top, no header).
  const selectedOptions = filteredOptions.filter(o => selectedSet.has(o.tag));
  const unselectedOptions = filteredOptions.filter(
    o => !selectedSet.has(o.tag)
  );

  const renderChip = (opt: TagOption) => (
    <TagChip
      key={opt.tag}
      option={opt}
      on={selectedSet.has(opt.tag)}
      dim={
        dimUnavailable &&
        selected.length === 0 &&
        !selectedSet.has(opt.tag) &&
        !availableInView.has(opt.tag)
      }
      touch={sheet}
      color={colorFor && colorFor(opt.tag)}
      onToggle={onToggle}
    />
  );

  return (
    <>
      {showTypeahead && (
        <div className='relative mb-3'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <input
            type='text'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            // `globals.css` puts `ring-2 ring-ring ring-offset-2` on every
            // focus-visible input via a base-layer rule - a harsh halo
            // around a text field. Override with `!important` to suppress;
            // border color (indigo on focus) is the focus indicator.
            className='w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground !outline-none placeholder:text-muted-foreground/60 focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 dark:focus:border-primary max-sm:h-11 max-sm:text-base'
          />
        </div>
      )}

      {filteredOptions.length === 0 ? (
        <p className='py-1 text-xs text-muted-foreground'>
          No tags match your search
        </p>
      ) : isLarge ? (
        <div className={`flex flex-col gap-3 ${maxHCls}`}>
          {selectedOptions.length > 0 && (
            <div>
              <div className='mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                <span>Selected</span>
                <span className='tabular-nums text-foreground'>
                  {selectedOptions.length}
                </span>
              </div>
              <div className='flex flex-wrap gap-1.5'>
                {selectedOptions.map(renderChip)}
              </div>
            </div>
          )}
          {unselectedOptions.length > 0 && (
            <div>
              {selectedOptions.length > 0 && (
                <div className='mb-1.5 border-t border-border pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Available
                </div>
              )}
              <div className='flex flex-wrap gap-1.5'>
                {unselectedOptions.map(renderChip)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <FlatChips
          options={filteredOptions}
          renderChip={renderChip}
          className={maxHCls}
          limit={sheetLimit({ sheet, expanded, search, selected })}
          onShowAll={() => setExpanded(true)}
        />
      )}
    </>
  );
}

/** How many chips the sheet shows: all while searching, else the first few
 *  and never fewer than are selected. */
function sheetLimit(o: {
  sheet: boolean;
  expanded: boolean;
  search: string;
  selected: string[];
}) {
  if (!o.sheet || o.expanded || o.search.trim()) return null;
  return Math.max(SHEET_TAGS, o.selected.length);
}

/** One tag: a check when on, the domain swatch (if any) when off, a count. */
function TagChip({
  option: { tag, count },
  on,
  dim,
  touch,
  color,
  onToggle,
}: {
  option: TagOption;
  on: boolean;
  dim: boolean;
  touch: boolean;
  color?: string;
  onToggle: (tag: string) => void;
}) {
  return (
    <button
      type='button'
      onClick={() => onToggle(tag)}
      aria-pressed={on}
      title={`${count} concept${count === 1 ? '' : 's'}`}
      className={filterChip({ on, dim, size: touch ? 'touch' : 'sm' })}
    >
      {on ? (
        <Check className='h-3 w-3 flex-shrink-0' aria-hidden='true' />
      ) : (
        color && (
          <span
            aria-hidden='true'
            className='h-1.5 w-1.5 rounded-full'
            style={{ backgroundColor: color }}
          />
        )
      )}
      <span>{tag}</span>
      <span className={chipCount(on)}>{count}</span>
    </button>
  );
}

/**
 * The wrapping chip list. With a `limit` (the phone sheet) it shows that many
 * - the order already puts selected tags first - and a "Show all N" after,
 * instead of capping the list in a scroller inside the sheet's own.
 */
function FlatChips({
  options,
  renderChip,
  className,
  limit,
  onShowAll,
}: {
  options: TagOption[];
  renderChip: (opt: TagOption) => React.ReactNode;
  className: string;
  limit: number | null;
  onShowAll: () => void;
}) {
  const shown = limit == null ? options : options.slice(0, limit);
  return (
    <>
      <div className={`flex flex-wrap gap-1.5 ${className}`}>
        {shown.map(renderChip)}
      </div>
      {shown.length < options.length && (
        <button
          type='button'
          onClick={onShowAll}
          className='mt-1 flex h-11 items-center text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          Show all {options.length}
        </button>
      )}
    </>
  );
}

/**
 * Toggle a tag in/out of a selection list. Pure helper used by the
 * per-section filter wrappers so they don't each reimplement the same
 * include/exclude pattern.
 */
export function toggleTagInList(list: string[], tag: string): string[] {
  return list.includes(tag) ? list.filter(t => t !== tag) : [...list, tag];
}
